import { useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface AlertItem {
  id: string;
  level: 'info' | 'warn' | 'error';
  message: string;
  timestamp: string;
  source: string;
}

interface NotificationRow {
  id: string;
  type: string;
  title: string;
  body: string;
  created_at: string;
  surface?: string | null;
}

interface RecentNotificationsResponse {
  notifications: NotificationRow[];
  total: number;
}

const LEVEL_COLORS: Record<string, string> = {
  info: 'var(--sp-accent)',
  warn: 'var(--sp-warn)',
  error: 'var(--sp-error)',
};

const LEVEL_LABELS: Record<string, string> = {
  info: 'INFO',
  warn: 'WARN',
  error: 'ERR',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function AlertsWidget({ context }: WidgetProps) {
  const [alerts, setAlerts] = useState<AlertItem[] | null>(null);
  const [endpointMissing, setEndpointMissing] = useState(false);

  useEffect(() => {
    // Try notifications endpoint
    void engineFetchJson<RecentNotificationsResponse>('/notifications/recent')
      .then((data) => {
        const items = (data.notifications ?? []).map<AlertItem>((item) => ({
          id: item.id,
          level: item.type === 'error' ? 'error' : item.type === 'warn' || item.type === 'warning' ? 'warn' : 'info',
          message: item.body?.trim() ? `${item.title}: ${item.body}` : item.title,
          timestamp: item.created_at,
          source: item.surface || item.type || 'notification',
        }));
        setAlerts(items);
        setEndpointMissing(false);
      })
      .catch(() => {
        // Keep the widget informative when the notifications surface is unavailable.
        setEndpointMissing(true);
        setAlerts([]);
      });
  }, [context.authToken]);

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Alerts</span>
        <a href="/settings/notifications" style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          Preferences
        </a>
      </div>
      <div className="sp-widget-body" style={{ padding: '4px 12px' }}>
        {endpointMissing ? (
          <div>
            <div style={{ color: 'var(--sp-warn)', fontSize: 12, marginBottom: 4 }}>
              Awaiting engine endpoint
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginBottom: 8 }}>
              GET /api/v1/notifications/recent
            </div>
            {/* Mock alert layout */}
            <div style={{ opacity: 0.35 }}>
              {[
                { level: 'info', msg: 'Codex ingest completed: 42 chunks', time: '10m ago' },
                { level: 'warn', msg: 'Budget 80% consumed for this cycle', time: '2h ago' },
                { level: 'error', msg: 'Twitter source sync failed', time: '5h ago' },
              ].map((a, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    padding: '4px 0',
                    fontSize: 12,
                    borderBottom: '1px solid var(--sp-border)',
                  }}
                >
                  <span
                    className="mono"
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: LEVEL_COLORS[a.level],
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  >
                    {LEVEL_LABELS[a.level]}
                  </span>
                  <span style={{ flex: 1, color: 'var(--sp-text-muted)' }}>{a.msg}</span>
                  <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', flexShrink: 0 }}>
                    {a.time}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginTop: 6 }}>
              Mock layout above. Will display real alerts when endpoint ships.
            </div>
          </div>
        ) : alerts && alerts.length === 0 ? (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12, padding: '8px 0' }}>
            No active alerts. All systems nominal.
          </div>
        ) : alerts ? (
          <div style={{ maxHeight: 200, overflow: 'auto' }}>
            {alerts.map((a) => (
              <div
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '4px 0',
                  fontSize: 12,
                  borderBottom: '1px solid var(--sp-border)',
                }}
              >
                <span
                  className="mono"
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: LEVEL_COLORS[a.level] ?? 'var(--sp-text-dim)',
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                >
                  {LEVEL_LABELS[a.level] ?? a.level}
                </span>
                <span style={{ flex: 1 }}>{a.message}</span>
                <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', flexShrink: 0 }}>
                  {timeAgo(a.timestamp)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>Loading alerts...</div>
        )}
      </div>
    </div>
  );
}

export const alertsWidget: WidgetDefinition = {
  id: 'alerts',
  name: 'Alerts',
  description: 'System alerts, notifications, and operational warnings.',
  icon: 'bell',
  category: 'universal',
  scope: 'both',
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 3, h: 3 },
  component: AlertsWidget,
};
